import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Mail } from "lucide-react";
import "./plansBilling.css";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { SUPPORT_EMAIL, legalLinks } from "../common/SiteFooter";
import { formatDay, resolveSubState, useRegionSubs } from "./planState";
import { getUserPlan } from "../../utils/planAccess";
import { formatPlanPrice as formatUsdPrice } from "../landing/PlansSection";
import { PLAN_PRICES, type PlanCurrency } from "../landing/landingContent";
import { WORLD_REGIONS, getRegion } from "../../utils/regions";

type Cycle = "monthly" | "annual";

interface Plan {
  _id: string;
  region?: string;
  tier?: string;
  price?: string | number;
  currency?: string;
  billingCadence?: string;
  isActive?: boolean;
}

const CADENCE: Record<Cycle, string> = {
  monthly: "per month",
  annual: "per year",
};

const DEFAULT_REGION = "healjai";

const CURRENCIES: PlanCurrency[] = ["USD", "THB"];

/** Finds the live Pro or Global plan for a currency + billing cycle. */
const findPlan = (
  plans: Plan[],
  tier: "pro" | "global",
  cycle: Cycle,
  currency: PlanCurrency,
): Plan | undefined =>
  plans.find(
    (p) =>
      p?._id &&
      p.isActive !== false &&
      p.tier === tier &&
      String(p.currency || "").toUpperCase() === currency &&
      String(p.billingCadence || "").toLowerCase() === CADENCE[cycle],
  );

const savingPercent = (monthly: number, yearly: number): number =>
  Math.max(0, Math.round((1 - yearly / (monthly * 12)) * 100));

/** Flat, full-width page footer: legal links, support contact and payment note. */
export const PlansFooter: React.FC = () => {
  const { t } = useTranslation();
  return (
    <footer className="pb-root pb-footer">
      <div className="pb-footer__inner">
        <div className="pb-footer__grid">
          <div>
            <div className="pb-logo">
              <span className="pb-logo__mark" aria-hidden="true" />
              Astria
            </div>
            <p className="pb-footer__about">
              {t(
                "astria.planFooterAbout",
                "Guidance for your journey toward clarity and purpose. Upgrade, pause, or cancel your plan at any time.",
              )}
            </p>
          </div>

          <div>
            <h3 className="pb-footer__title">
              {t("astria.planLegal", "Legal")}
            </h3>
            <ul className="pb-footer__links">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to}>{t(l.labelKey, l.label) as string}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="pb-footer__title">
              {t("astria.planContact", "Contact")}
            </h3>
            <p className="pb-footer__contact">
              {t(
                "astria.planFooterContact",
                "Questions about billing, refunds, or your subscription? We typically reply within 2 business days.",
              )}
            </p>
            <a className="pb-footer__mail" href={`mailto:${SUPPORT_EMAIL}`}>
              <Mail size={15} aria-hidden="true" />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>

        <div className="pb-footer__end">
          <p>
            © {new Date().getFullYear()} Astria ·{" "}
            {t(
              "astria.planFooterNote",
              "For reflection and self-discovery, not professional advice.",
            )}
          </p>
          <div className="pb-footer__badges">
            <span>
              <Lock size={14} aria-hidden="true" />
              {t("astria.planSecure", "Secure by design")}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {t("astria.planStripe", "Payments processed by Stripe")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

/**
 * Account → Plans & Billing. Shows a Free and a Pro card for the region the
 * user picked, priced in that region's currency from the live plan records
 * (so the number shown is the number Stripe charges), then hands off to
 * Stripe Checkout with just a planId.
 */
const PlansBilling: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const initialRegion = useMemo(() => {
    const wanted = searchParams.get("region") || user?.region || DEFAULT_REGION;
    return getRegion(wanted)?.value || DEFAULT_REGION;
  }, [searchParams, user?.region]);

  const regionValue = initialRegion;
  const [currency, setCurrency] = useState<PlanCurrency>(
    initialRegion === "healjai" || initialRegion === "thailand" ? "THB" : "USD",
  );
  const [cycle, setCycle] = useState<Cycle>("annual");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  // Which card started checkout; the other card stays idle (but locked) meanwhile.
  const [redirectingTo, setRedirectingTo] = useState<"pro" | "global" | null>(
    null,
  );
  const isRedirecting = redirectingTo !== null;
  const [checkoutError, setCheckoutError] = useState("");
  const { subs } = useRegionSubs();

  const loadPlans = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await api.get("/backend/subscriptionPlans");
      const raw = res?.data?.data || res?.data?.plans || res?.data || [];
      setPlans(Array.isArray(raw) ? raw : []);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const region = getRegion(regionValue) || WORLD_REGIONS[0];
  const regionName = t(
    `astriaHome.regionName.${region.value}`,
    region.label,
  ) as string;
  const plan = findPlan(plans, "pro", cycle, currency);

  const proPrices = PLAN_PRICES[currency].pro;
  const globalPrices = PLAN_PRICES[currency].global;
  const savePct = savingPercent(proPrices.monthly, proPrices.yearly);
  const globalSavePct = savingPercent(globalPrices.monthly, globalPrices.yearly);
  const cyclePrice = (
    prices: { monthly: number; yearly: number },
    c: Cycle,
  ): string =>
    formatUsdPrice(c === "monthly" ? prices.monthly : prices.yearly, currency);

  const { sub, owned, expired, cancelled, daysLeft } = resolveSubState(
    subs,
    region.value,
    user,
  );

  const startCheckout = async (
    which: "pro" | "global",
    target: Plan | undefined,
  ) => {
    if (!target?._id || isRedirecting) return;
    setCheckoutError("");
    setRedirectingTo(which);
    try {
      const res = await api.post("/payment/checkout", { planId: target._id });
      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setCheckoutError(t("astriaHome.journey.stripeMissing") as string);
    } catch (err: any) {
      setCheckoutError(
        err?.response?.data?.message ||
          err?.message ||
          (t("astriaHome.journey.checkoutFailed") as string),
      );
    } finally {
      setRedirectingTo(null);
    }
  };

  const currentPlan = getUserPlan(user);
  const isGlobalUser = currentPlan === "global";
  const proDisabled =
    owned || isGlobalUser || isRedirecting || status !== "ready" || !plan;

  // Global is one worldwide plan: it has its own record (region "global").
  const globalPlan = findPlan(plans, "global", cycle, currency);

  const priceCard = (
    prices: { monthly: number; yearly: number },
    save: number,
  ) => (
    <div className="pb-price">
      {cycle === "annual" && save > 0 && (
        <em className="pb-ribbon">
          {t("astria.planSave", "Save")} {save}%
        </em>
      )}
      <span>
        {cycle === "monthly"
          ? t("astria.planMonthly", "Monthly")
          : t("astria.planYearly", "Yearly")}
      </span>
      <b>{cyclePrice(prices, cycle)}</b>
      <small>
        {cycle === "monthly"
          ? t("astria.planPerMonth", "per month")
          : t("astria.planPerYear", "per year")}
      </small>
    </div>
  );

  return (
    <div className="pb-root">
      <div className="pb-hero">
        <p>{t("astria.planHeroA", "Most people come to Astria for answers.")}</p>
        <p>{t("astria.planHeroB", "They stay to understand the patterns beneath them.")}</p>
      </div>
      <div className="pb-head">
        <h2 className="pb-title">
          {t("astria.planHeadA", "Choose how deep")}{" "}
          <em>{t("astria.planHeadB", "you want to go.")}</em>
        </h2>
      </div>

      <div className="pb-region">
        <div className="pb-currency" role="group" aria-label="Currency">
          {CURRENCIES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={currency === value}
              className={currency === value ? "is-on" : ""}
              onClick={() => {
                setCurrency(value);
                setCheckoutError("");
              }}
            >
              {value}
            </button>
          ))}
        </div>
        <div
          className="pb-currency pb-cycle"
          role="radiogroup"
          aria-label={t("astria.planBilling", "Billing cycle") as string}
        >
          {(["monthly", "annual"] as Cycle[]).map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={cycle === c}
              className={cycle === c ? "is-on" : ""}
              onClick={() => setCycle(c)}
            >
              {c === "monthly"
                ? t("astria.planMonthly", "Monthly")
                : t("astria.planYearly", "Annual")}
              {c === "annual" && savePct > 0 && (
                <em className="pb-cycle__save">-{savePct}%</em>
              )}
            </button>
          ))}
        </div>
      </div>

      {status === "error" && (
        <div className="pb-retry" role="alert">
          <p>
            {t("astria.planLoadError", "We could not load plans right now.")}
          </p>
          <button type="button" onClick={loadPlans}>
            {t("astria.planRetry", "Try again")}
          </button>
        </div>
      )}

      <div className="pb-plans">
        <section
          className="pb-plan"
          aria-label={t("astria.planFree", "Free") as string}
        >
          <span className="pb-kicker">{t("astria.planFree", "Free")}</span>
          <h3 className="pb-name">
            {t("astria.planFreeName", "Start with Astria")}
          </h3>
          <p className="pb-tagline">{t("astria.planFreeSummary", "Explore the surface.")}</p>
          <div className="pb-prices pb-prices--single">
            <div className="pb-price">
              <span>{t("astria.planFree", "Free")}</span>
              <b>{formatUsdPrice(0, currency)}</b>
              <small>{t("astria.planPerMonth", "per month")}</small>
            </div>
          </div>
          <ul className="pb-features">
            <li>{t("astria.planFreeG1", "Daily Guidance")}</li>
            <li>{t("astria.planFreeG2", "Daily Flow")}</li>
            <li>{t("astria.planFreeG3", "Life Blueprint preview")}</li>
            <li>{t("astria.planFreeG4", "Relationship preview")}</li>
            <li>{t("astria.planFreeG5", "Compatibility preview")}</li>
            <li>{t("astria.planFreeG6", "15 messages per day")}</li>
            <li>{t("astria.planFreeG7", "3-day chat history")}</li>
          </ul>
          <button type="button" className="pb-cta" disabled>
            {owned || isGlobalUser
              ? isGlobalUser
                ? t("astria.planIncludedGlobal", "Included in Global")
                : t("astria.planIncluded", "Included in Pro")
              : t("astria.planCurrent")}
          </button>
        </section>

        <section
          className="pb-plan pb-plan--pro"
          aria-label={t("astria.planProKicker", "Pro") as string}
        >
          <div className="pb-kickrow">
            <span className="pb-kicker">
              {t("astria.planProKicker", "Pro · Your Life GPS")}
            </span>
            {sub && (
              <span
                className={`pb-pill ${expired ? "pb-pill--expired" : "pb-pill--active"}`}
              >
                {expired
                  ? t("astria.planStateExpired", "Expired")
                  : cancelled
                    ? t("astria.planStateCancelled", "Cancelled")
                    : t("astria.planStateActive", "Active")}
              </span>
            )}
          </div>
          <h3 className="pb-name">{t("astria.planPro")}</h3>
          <p className="pb-tagline">{t("astria.planProSummary", "Discover the pattern.")}</p>
          {sub && (
            <div className="pb-status" role="status">
              <p className="pb-status__main">
                {expired
                  ? t("astria.planSubExpiredOn", "Expired on")
                  : cancelled
                    ? t("astria.planSubEndsOn", "Ends on")
                    : t("astria.planSubRenewsOn", "Renews on")}{" "}
                <strong>{formatDay(sub.endDate)}</strong>
              </p>
              <p className="pb-status__meta">
                {sub.planName ||
                  (sub.cadence === "annual"
                    ? t("astria.planYearly", "Yearly")
                    : t("astria.planMonthly", "Monthly"))}
                {" · "}
                {t("astria.planSubStarted", "Started")}{" "}
                {formatDay(sub.startDate)}
                {owned && daysLeft !== null && (
                  <>
                    {" · "}
                    {t("astria.planSubDaysLeft", {
                      count: daysLeft,
                      defaultValue: "{{count}} days left",
                    })}
                  </>
                )}
              </p>
              {expired && (
                <p className="pb-status__hint">
                  {t(
                    "astria.planExpiredHint",
                    "Your Pro access has ended. Renew to open it again. Everything you saved is still here.",
                  )}
                </p>
              )}
            </div>
          )}
          {!owned && <div className="pb-prices">{priceCard(proPrices, savePct)}</div>}
          <ul className="pb-features">
            <li>{t("astria.planProG1", "Unlimited chat")}</li>
            <li>{t("astria.planProG2", "Unlimited chat history")}</li>
            <li>{t("astria.planProG3", "Full Life Blueprint")}</li>
            <li>{t("astria.planProG4", "Full Compatibility")}</li>
            <li>{t("astria.planProG5", "Relationship Insights")}</li>
            <li>{t("astria.planProG6", "Life Direction")}</li>
            <li>{t("astria.planProG7", "Letter Never Sent")}</li>
            <li>{t("astria.planProG8", "PDF reports")}</li>
            <li>
              {t("astria.planProG9", {
                region: regionName,
                defaultValue: "Full {{region}} collection",
              })}
            </li>
            <li>{t("astria.planProG10", "2 Global Passes per month (24 hours each)")}</li>
          </ul>

          <button
            type="button"
            className="pb-cta pb-cta--primary"
            onClick={() => startCheckout("pro", plan)}
            disabled={proDisabled}
          >
            {isGlobalUser
              ? t("astria.planIncludedGlobal", "Included in Global")
              : owned
              ? t("astria.planActive", "Your plan")
              : redirectingTo === "pro"
                ? t("astria.planRedirecting", "Opening checkout…")
                : expired
                  ? (t("astria.planRenew", "Renew Pro") as string)
                  : (t("astria.planUnlock", "Unlock Pro") as string)}
          </button>

          {status === "ready" && !plan && !owned && (
            <p className="pb-note" role="status">
              {t(
                "astria.planUnavailable",
                "This plan is not available yet for this region.",
              )}
            </p>
          )}
          {checkoutError && (
            <p className="pb-note pb-note--error" role="alert">
              {checkoutError}
            </p>
          )}
        </section>

        <section
          className="pb-plan pb-plan--global"
          aria-label={t("astria.planGlobal", "Global") as string}
        >
          <div className="pb-kickrow">
            <span className="pb-kicker">
              {t("astria.planGlobalKicker", "Global · Every region")}
            </span>
            {isGlobalUser ? (
              <span className="pb-pill pb-pill--active">
                {t("astria.planStateActive", "Active")}
              </span>
            ) : (
              <em className="pb-badge">
                {t("astria.planBestValue", "Best value")}
              </em>
            )}
          </div>
          <h3 className="pb-name">{t("astria.planGlobal", "Global")}</h3>
          <p className="pb-tagline">
            {t(
              "astria.planGlobalDesc",
              "Wisdom from around the world, in one plan.",
            )}
          </p>
          {!isGlobalUser && (
            <div className="pb-prices">
              {priceCard(globalPrices, globalSavePct)}
            </div>
          )}
          <ul className="pb-features">
            <li>{t("astria.planGlobalF1", "Everything in Pro")}</li>
            <li>
              {t("astria.planGlobalF2", {
                count: WORLD_REGIONS.length,
                defaultValue: "All {{count}} regions unlocked",
              })}
            </li>
            <li>
              {t("astria.planGlobalF3", "Switch region any time, no passes")}
            </li>
            <li>{t("astria.planGlobalG1", "Energy Match included")}</li>
            <li>
              {t("astria.planGlobalF4", "New regions included as they launch")}
            </li>
            <li>{t("astria.planGlobalG2", "China BaZi")}</li>
            <li>{t("astria.planGlobalG3", "France Big 3")}</li>
            <li>{t("astria.planGlobalG4", "Germany Career Direction")}</li>
            <li>{t("astria.planGlobalG5", "Italy Relationship Snapshot")}</li>
            <li>{t("astria.planGlobalG6", "Russia Destiny Themes")}</li>
          </ul>
          <button
            type="button"
            className="pb-cta pb-cta--primary"
            onClick={() => startCheckout("global", globalPlan)}
            disabled={
              isGlobalUser || isRedirecting || status !== "ready" || !globalPlan
            }
          >
            {isGlobalUser
              ? t("astria.planActive", "Your plan")
              : redirectingTo === "global"
                ? t("astria.planRedirecting", "Opening checkout…")
                : status === "ready" && !globalPlan
                  ? t("astria.planComingSoon", "Coming soon")
                  : t("astria.planUnlockGlobal", "Unlock Global")}
          </button>
        </section>
      </div>

      <p className="pb-fine">
        {t("astria.planFine", "Prices in local currency · Cancel anytime")}
      </p>
    </div>
  );
};

export default PlansBilling;
